// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file LayoutContent.tsx
 * @input Uses React StyleX, LayoutSlotsContext
 * @output Exports LayoutContent component and LayoutContentProps
 * @position Scrollable main content area for Layout. Wraps the primary body content
 *   with automatic scroll containment and context-aware padding.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Layout/Layout.doc.mjs
 * - /apps/storybook/stories/Layout.stories.tsx
 * - /packages/cli/templates/blocks/components/Layout/ (showcase blocks)
 */
import { use } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { LayoutSlotsContext } from "./LayoutSlotsContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { paddingStyles, containerPaddingInlineVarStyles, containerPaddingBlockStartVarStyles, containerPaddingBlockEndVarStyles } from "./padding.stylex.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  content: {
    kB7OPa: "x9f619",
    kZKoxP: "x5yr21d",
    kUk6DE: "x98rzlu",
    kAzted: "x2lwn1j",
    kVQacm: "x7giv3",
    kZCmMZ: "xwjyata",
    kwRFfy: "x1peupej",
    kLKAdn: "xqty4a x1ioh5az",
    kGO01o: "xg476vw xsg11nj",
    "--container-padding-inline-start": "x408pgh",
    "--container-padding-inline-end": "xikqloz",
    "--container-padding-block-start": "xjmgx01",
    "--container-padding-block-end": "xi9ns85",
    $$css: true
  },
  noStart: {
    kZCmMZ: "x139j0dd",
    "--container-padding-inline-start": "xdvaxxn",
    "--container-padding-inline-end": "xqpvj4r",
    $$css: true
  },
  noEnd: {
    kwRFfy: "xpc6k2p",
    $$css: true
  },
  noHeader: {
    kLKAdn: "x81pis9",
    "--container-padding-block-start": "xzz8v79",
    $$css: true
  },
  noFooter: {
    kGO01o: "xon7vh3",
    "--container-padding-block-end": "x1xjq73n",
    $$css: true
  },
  scrollable: {
    kVQacm: "xysyzu8",
    $$css: true
  },
  fullBleed: {
    kZCmMZ: "x1c1uobl",
    kwRFfy: "xyri2b",
    kLKAdn: "xexx8yu",
    kGO01o: "x18d9i69",
    "--container-padding-inline-start": "xrhngw9",
    "--container-padding-inline-end": "xjsfl84",
    "--container-padding-block-start": "x1047aw6",
    "--container-padding-block-end": "xax9j7h",
    $$css: true
  }
};
/**
 * Scrollable main content area for Layout. Wraps the primary body content
 * with automatic scroll containment and context-aware padding.
 *
 * Already provides its own padding and scroll — don't add padding or
 * overflow to children. Use `padding={0}` if you need edge-to-edge content.
 *
 * @example
 * ```
 * <LayoutContainer variant="card">
 *   <Layout
 *     header={<LayoutHeader>Title</LayoutHeader>}
 *     content={<LayoutContent>Main body content</LayoutContent>}
 *   />
 * </LayoutContainer>
 * <LayoutContainer variant="card">
 *   <Layout
 *     content={
 *       <LayoutContent padding={0}>
 *         <Table />
 *       </LayoutContent>
 *     }
 *   />
 * </LayoutContainer>
 * <LayoutContainer variant="card">
 *   <Layout
 *     content={
 *       <LayoutContent isScrollable={false}>
 *         <StickyElement />
 *       </LayoutContent>
 *     }
 *   />
 * </LayoutContainer>
 * ```
 */
export function LayoutContent({
  children,
  isScrollable = true,
  padding,
  label,
  role,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const {
    hasHeader,
    hasFooter,
    hasStart,
    hasEnd
  } = use(LayoutSlotsContext);
  const isZeroPadding = padding === 0;
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    role: role,
    "aria-label": label,
    ...mergeProps(themeProps('layout-content'), stylex.props(styles.content,
    // Outer padding on container edges (unless content is full bleed)
    !hasStart && !isZeroPadding && padding == null && styles.noStart, !hasEnd && !isZeroPadding && padding == null && styles.noEnd, !hasHeader && !isZeroPadding && padding == null && styles.noHeader, !hasFooter && !isZeroPadding && padding == null && styles.noFooter, isScrollable && styles.scrollable, isZeroPadding && styles.fullBleed, padding != null && paddingStyles[padding], padding != null && containerPaddingInlineVarStyles[padding], padding != null && containerPaddingBlockStartVarStyles[padding], padding != null && containerPaddingBlockEndVarStyles[padding], xstyle), className, style),
    ...props,
    children: children
  });
}
LayoutContent.displayName = 'LayoutContent';